import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import RouteSeo from "@/components/Seo";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/shared";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";

import Home from "@/pages/marketing/Home";
import Pricing from "@/pages/marketing/Pricing";
import Features from "@/pages/marketing/Features";
import Security from "@/pages/marketing/Security";
import HowItWorks from "@/pages/marketing/HowItWorks";
import ProxmoxBackup from "@/pages/marketing/ProxmoxBackup";
import MSP from "@/pages/marketing/MSP";
import Docs from "@/pages/marketing/Docs";
import StatusPage from "@/pages/marketing/StatusPage";
import Contact from "@/pages/marketing/Contact";
import Legal from "@/pages/marketing/Legal";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";

import AppLayout from "@/pages/app/AppLayout";
import Dashboard from "@/pages/app/Dashboard";
import Deploy from "@/pages/app/Deploy";
import ServerDetail from "@/pages/app/ServerDetail";
import Restore from "@/pages/app/Restore";
import Alerts from "@/pages/app/Alerts";
import Audit from "@/pages/app/Audit";
import Billing from "@/pages/app/Billing";
import WireGuard from "@/pages/app/WireGuard";
import Tickets from "@/pages/app/Tickets";
import Settings from "@/pages/app/Settings";
import PaymentResult from "@/pages/app/PaymentResult";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminInfrastructure from "@/pages/admin/AdminInfrastructure";
import AdminProviders from "@/pages/admin/AdminProviders";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminEconomics from "@/pages/admin/AdminEconomics";

function MarketingLayout() {
  return (
    <div className="min-h-screen bg-[#06080D]">
      <Navbar />
      <Outlet />
      <Footer />
    </div>
  );
}

function Protected({ admin }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.platform_role !== "admin") return <Navigate to="/app/dashboard" replace />;
  return <Outlet />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-right" richColors />
      <BrowserRouter>
        <RouteSeo />
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/security" element={<Security />} />
            <Route path="/proxmox-backup" element={<ProxmoxBackup />} />
            <Route path="/msp" element={<MSP />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/:doc" element={<Legal />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          <Route element={<Protected />}>
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="deploy" element={<Deploy />} />
              <Route path="servers/:id" element={<ServerDetail />} />
              <Route path="restore" element={<Restore />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="audit" element={<Audit />} />
              <Route path="billing" element={<Billing />} />
              <Route path="wireguard" element={<WireGuard />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Route>

          <Route element={<Protected admin />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="infrastructure" element={<AdminInfrastructure />} />
              <Route path="providers" element={<AdminProviders />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="economics" element={<AdminEconomics />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
